/**
 * Layout do Dashboard
 * Layout principal com sidebar e navegação
 */

import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Camera, 
  LogOut, 
  Menu, 
  X,
  Moon,
  Sun,
  Building2,
  Globe,
  Key,
  Mail,
  AlertTriangle,
  BarChart3,
  Clock,
  CreditCard,
  ChevronDown,
  Layers,
  FolderTree,
  FileSpreadsheet,
  Star,
  Cpu,
  Flag,
  Flame,
  Car,
  Shield,
  HelpCircle,
  WifiOff,
  Shirt,
  Wrench
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useConfirmModal } from './ConfirmModal';
import { OperatingStatusBadge } from './OperatingStatusBadge';
import { OfflineCamerasBanner } from './OfflineCamerasBanner';
import { environment } from '../config/environment';

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout, isAdmin, organization, organizationName, isGroupUser, group, groupOrganizations, switchOrganization, activeOrganizationId } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const { showConfirm, ModalComponent } = useConfirmModal();
  const [organizationType, setOrganizationType] = useState<string | null>(null);
  const [trialInfo, setTrialInfo] = useState<{ is_trial: boolean; trial_status: string | null; days_remaining_trial: number | null } | null>(null);
  // 🚫 Flag de Exclusão de Funcionários — menu só aparece quando ativa (org OU grupo)
  const [employeeFeatureEnabled, setEmployeeFeatureEnabled] = useState(false);

  // Buscar tipo da organização para filtrar menu
  const effectiveOrgId = activeOrganizationId || user?.organization_id;
  useEffect(() => {
    async function fetchOrgType() {
      if (!effectiveOrgId || user?.is_superuser) return;
      const token = localStorage.getItem('awflux_token');
      const activeOrgForHeader = localStorage.getItem('awflux_active_organization_id');
      const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` };
      if (activeOrgForHeader) headers['X-Organization-Id'] = activeOrgForHeader;

      try {
        const res = await fetch(`${environment.apiBaseUrl}/api/v1/organizations/${effectiveOrgId}`, { headers });
        if (res.ok) {
          const data = await res.json();
          setOrganizationType(data.organization_type || 'shopping');
        }
      } catch {
        // silencioso — não bloquear o layout por isso
      }

      // 🚫 Efetivo da exclusão de funcionários (org OU grupo) p/ mostrar/esconder menu
      try {
        const settingsRes = await fetch(
          `${environment.apiBaseUrl}/api/v1/organizations/${effectiveOrgId}/employees/settings`,
          { headers }
        );
        if (settingsRes.ok) {
          const settings = await settingsRes.json();
          setEmployeeFeatureEnabled(!!settings.effective_enabled);
        }
      } catch {
        // silencioso
      }
    }
    fetchOrgType();
  }, [effectiveOrgId, user?.is_superuser]);

  // Buscar status de trial para exibir banner
  useEffect(() => {
    async function fetchTrialStatus() {
      if (!effectiveOrgId || user?.is_superuser) return;
      try {
        const token = localStorage.getItem('awflux_token');
        const activeOrgForHeader = localStorage.getItem('awflux_active_organization_id');
        const headers: Record<string, string> = { 'Authorization': `Bearer ${token}` };
        if (activeOrgForHeader) headers['X-Organization-Id'] = activeOrgForHeader;
        const res = await fetch(`${environment.apiBaseUrl}/api/v1/billing/status`, { headers });
        if (res.ok) {
          const data = await res.json();
          setTrialInfo({
            is_trial: data.is_trial || false,
            trial_status: data.trial_status || null,
            days_remaining_trial: data.days_remaining_trial ?? null,
          });
        }
      } catch {
        // silencioso
      }
    }
    fetchTrialStatus();
  }, [effectiveOrgId, user?.is_superuser]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setOrgDropdownOpen(false);
      }
    }
    if (orgDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [orgDropdownOpen]);

  const handleLogout = () => {
    showConfirm({
      title: 'Sair do Sistema',
      message: 'Tem certeza que deseja sair? Você precisará fazer login novamente para acessar o sistema.',
      confirmText: 'Sim, sair',
      cancelText: 'Cancelar',
      type: 'warning',
      icon: 'logout',
      onConfirm: () => {
        logout();
        navigate('/login');
      }
    });
  };

  const menuItems = [
    { name: 'Meu dashboard', path: '/partner', icon: LayoutDashboard, adminOnly: false, partnerOnly: true, allowedRoles: ['partner'] },
    { name: 'Minhas organizações', path: '/partner/organizations', icon: Building2, adminOnly: false, partnerOnly: true, allowedRoles: ['partner'] },
    { name: 'Minhas comissões', path: '/partner/commissions', icon: CreditCard, adminOnly: false, partnerOnly: true, allowedRoles: ['partner'] },
    { name: 'Meus pagamentos', path: '/partner/payments', icon: FileSpreadsheet, adminOnly: false, partnerOnly: true, allowedRoles: ['partner'] },
    { name: 'Dados para recebimento', path: '/partner/payment-details', icon: Key, adminOnly: false, partnerOnly: true, allowedRoles: ['partner'] },
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: LayoutDashboard,
      adminOnly: false,
      allowedRoles: [] as string[],  // Todos podem acessar
      hideSuperuser: true,  // Esconder para superuser
      hideForSuspectOnly: true,  // Esconder para orgs suspect_alerts_only
      hideForParking: true,  // Esconder para orgs parking
      hideForHeatmap: true,  // Esconder para orgs heatmap (mapa de calor exclusivo)
    },
    {
      name: 'Dashboard Global',
      path: '/super-admin',
      icon: Globe,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Estacionamento',
      path: '/parking',
      icon: Car,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator'] as string[],
      hideSuperuser: true,
      parkingOnly: true,  // Só aparece para orgs do tipo parking
      badge: 'Novo',
      badgeColor: 'bg-blue-600 text-white',
    },
    {
      name: 'Câmeras',
      path: '/cameras',
      icon: Camera,
      adminOnly: false,
      allowedRoles: ['admin'] as string[],  // Apenas admin
      hideSuperuser: true,  // Esconder para superuser
    },
    {
      name: 'Usuários',
      path: '/users',
      icon: Users,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator'] as string[],  // Admin, Manager e Coordinator
    },
    {
      name: 'Relatórios Gerenciais',
      path: '/reports',
      icon: FileSpreadsheet,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator', 'operator', 'viewer'] as string[],
      hideSuperuser: true,
    },
    {
      name: 'Agendamentos de Relatórios',
      path: '/report-schedules',
      icon: Mail,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator'] as string[],  // Admin, Manager e Coordinator
      hideSuperuser: true,
      hideForSuspectOnly: true,  // Esconder para orgs suspect_alerts_only
    },
    {
      name: 'Zonas de Calor',
      path: '/heatmap',
      icon: Flame,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator'] as string[],
      hideSuperuser: true,
      hideForSuspectOnly: true,
      hideForParking: true,  // Esconder para orgs parking
      badge: 'Novo',
      badgeColor: 'bg-orange-600 text-white',
    },
    {
      name: 'Funcionários / Fardas',
      path: '/employees',
      icon: Shirt,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator'] as string[],
      hideSuperuser: true,
      hideForSuspectOnly: true,
      hideForParking: true,
      employeeFeatureOnly: true,  // 🚫 Só aparece quando exclude_employees_enabled está ativa
      badge: 'Novo',
      badgeColor: 'bg-teal-600 text-white',
    },
    {
      name: 'Alertas de Suspeitos',
      path: '/suspect-alerts',
      icon: AlertTriangle,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator', 'operator'] as string[],  // Admin, Manager, Coordinator e Operator
      hideSuperuser: true,  // Esconder para superuser
      badge: 'Novo',
      badgeColor: 'bg-red-600 text-white',
    },
    {
      name: 'Central de Segurança',
      path: '/suspect-alerts-dashboard',
      icon: BarChart3,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator', 'operator'] as string[],
      hideSuperuser: true,
    },
    {
      name: 'Organizações',
      path: '/organizations',
      icon: Building2,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Grupos',
      path: '/organization-groups',
      icon: FolderTree,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Workers / Filas',
      path: '/workers-balance',
      icon: Cpu,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Recursos dos Workers',
      path: '/worker-resources',
      icon: Cpu,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],
    },
    {
      name: 'Nós de processamento',
      path: '/worker-nodes',
      icon: Cpu,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],
    },
    {
      name: 'Feature Flags',
      path: '/feature-flags',
      icon: Flag,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],
    },
    {
      name: 'Atividades Recentes',
      path: '/recent-activity',
      icon: Clock,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Gestão de Cobrança',
      path: '/billing-management',
      icon: CreditCard,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'EdgeAgents - Tokens',
      path: '/edge-agents',
      icon: Shield,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Câmeras Offline',
      path: '/camera-downtime',
      icon: WifiOff,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Auditoria de Lacunas',
      path: '/gap-backfill',
      icon: Wrench,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Parceiros e Comissões',
      path: '/partners',
      icon: Users,
      adminOnly: true,
      superuserOnly: true,
      allowedRoles: [] as string[],  // Apenas superuser
    },
    {
      name: 'Dados Históricos',
      path: '/historical-imports',
      icon: FileSpreadsheet,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator'] as string[],  // Admin, Gerente e Coordenador
      hideSuperuser: true,  // Não relevante para o Super Admin
      hideForParking: true,  // Esconder para orgs parking
    },
    {
      name: 'Observações de Dias',
      path: '/day-observations',
      icon: Star,
      adminOnly: false,
      allowedRoles: ['admin', 'manager', 'coordinator'] as string[],
      hideSuperuser: true,  // Não relevante para o Super Admin
      hideForParking: true,  // Esconder para orgs parking
    },
  ];

  const filteredMenuItems = menuItems.filter(item => {
    if (user?.role === 'partner') return item.partnerOnly === true;
    if (item.partnerOnly) return false;
    // Se tem hideSuperuser e o usuário é superuser, esconder
    if (item.hideSuperuser && user?.is_superuser) return false;
    
    // Se tem superuserOnly, verificar se é superuser
    if (item.superuserOnly && !user?.is_superuser) return false;

    // Se tem hideForSuspectOnly e org é suspect_alerts_only, esconder
    if (item.hideForSuspectOnly && organizationType === 'suspect_alerts_only') return false;

    // Se tem hideForParking e org é parking, esconder
    if (item.hideForParking && organizationType === 'parking') return false;

    // Se tem hideForHeatmap e org é heatmap, esconder
    if (item.hideForHeatmap && organizationType === 'heatmap') return false;

    // Se tem parkingOnly, só mostrar para orgs do tipo parking
    if (item.parkingOnly && organizationType !== 'parking') return false;

    // 🚫 Funcionários / Fardas: só aparece quando a flag de exclusão está ativa
    if (item.employeeFeatureOnly && !employeeFeatureEnabled) return false;
    
    // NOTA: Suspect Alerts sempre aparece no menu, mas página interna verifica permissão
    // e mostra tela de "Feature Premium Bloqueada" se suspect_alerts_enabled = false
    
    // Se tem allowedRoles, verificar se o usuário tem uma das roles
    if (item.allowedRoles && item.allowedRoles.length > 0) {
      const hasRole = user?.role && item.allowedRoles.includes(user.role);
      const isSuperuser = user?.is_superuser === true;
      return hasRole || isSuperuser;
    }
    
    // Fallback para adminOnly (compatibilidade)
    if (item.adminOnly && !isAdmin) return false;
    if (item.superuserOnly && !user?.is_superuser) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex md:flex-shrink-0 md:fixed md:inset-y-0 md:z-50">
        <div className="flex flex-col w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-colors">
          {/* Logo */}
          <div className="flex items-center h-16 px-4 sm:px-6 lg:px-8 border-b border-gray-200 dark:border-gray-700">
            <img src="/logo192.png" alt="AWFlux" className="w-8 h-8 mr-3 flex-shrink-0 rounded-lg" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">AWFlux</h1>
          </div>

          {/* Perfil do Usuário */}
          <div className="px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 dark:text-blue-300 font-semibold text-sm">
                  {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                </span>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {user?.role === 'partner' ? 'Parceiro comercial' : user?.role === 'admin' ? '👑 Administrador' : user?.role === 'manager' ? '👤 Gerente' : user?.role === 'coordinator' ? '🎯 Coordenador' : user?.role === 'operator' ? '⚙️ Operador' : '👁️ Visualizador'}
                </p>
              </div>
            </div>

            {/* Logo da Organização / Seletor de Grupo */}
            {isGroupUser ? (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                {/* Nome do Grupo */}
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="w-4 h-4 text-purple-500 dark:text-purple-400 flex-shrink-0" />
                  <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide truncate">
                    {group?.name || 'Grupo'}
                  </p>
                </div>
                {/* Dropdown de Organizações */}
                <div className="relative" ref={orgDropdownRef}>
                  <button
                    onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {organization?.logo_url && (
                        <img src={organization.logo_url} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
                      )}
                      <span className="text-gray-700 dark:text-gray-200 truncate">{organizationName}</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-1 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {orgDropdownOpen && (
                    <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                      {groupOrganizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => {
                            switchOrganization(org.id);
                            setOrgDropdownOpen(false);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition ${
                            activeOrganizationId === org.id
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 font-medium'
                              : 'text-gray-700 dark:text-gray-200'
                          }`}
                        >
                          {org.logo_url && (
                            <img src={org.logo_url} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                          )}
                          <span className="truncate">{org.name}</span>
                          {activeOrganizationId === org.id && (
                            <span className="ml-auto text-blue-500 flex-shrink-0">✓</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : organization?.logo_url ? (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <img
                    src={organization.logo_url}
                    alt={organizationName}
                    className="w-12 h-12 rounded-lg object-cover border-2 border-gray-200 dark:border-gray-700"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                      {organizationName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Sua Organização
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Menu de Navegação */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || (item.path === '/reports' && location.pathname.startsWith('/reports/'));

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                  {item.badge && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${item.badgeColor || 'bg-blue-600 text-white'}`}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Theme Toggle e Logout */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
            <button
              onClick={toggleTheme}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            >
              {isDark ? (
                <>
                  <Sun className="w-5 h-5 mr-3" />
                  Modo Claro
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5 mr-3" />
                  Modo Escuro
                </>
              )}
            </button>
            <Link
              to="/help"
              className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition ${
                location.pathname === '/help'
                  ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <HelpCircle className="w-5 h-5 mr-3" />
              Ajuda
            </Link>
            <Link
              to="/change-password"
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            >
              <Key className="w-5 h-5 mr-3" />
              Alterar Senha
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75"
            onClick={() => setSidebarOpen(false)}
          ></div>
          <aside className="fixed inset-y-0 left-0 flex flex-col w-64 bg-white dark:bg-gray-800">
            {/* Logo */}
            <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <img src="/logo192.png" alt="AWFlux" className="w-8 h-8 mr-3 flex-shrink-0 rounded-lg" />
                <h1 className="text-lg font-bold text-gray-900 dark:text-white">AWFlux</h1>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="ml-2 flex-shrink-0">
                <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Perfil do Usuário */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-300 font-semibold text-sm">
                    {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}
                  </span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.full_name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user?.role === 'partner' ? 'Parceiro comercial' : user?.role === 'admin' ? '👑 Administrador' : user?.role === 'manager' ? '👤 Gerente' : user?.role === 'coordinator' ? '🎯 Coordenador' : user?.role === 'operator' ? '⚙️ Operador' : '👁️ Visualizador'}
                  </p>
                </div>
              </div>

              {/* Logo da Organização / Seletor de Grupo - Mobile */}
              {isGroupUser ? (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4 text-purple-500 dark:text-purple-400 flex-shrink-0" />
                    <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide truncate">
                      {group?.name || 'Grupo'}
                    </p>
                  </div>
                  <select
                    value={activeOrganizationId || ''}
                    onChange={(e) => switchOrganization(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200"
                  >
                    {groupOrganizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              ) : organization?.logo_url ? (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <img
                      src={organization.logo_url}
                      alt={organizationName}
                      className="w-12 h-12 rounded-lg object-cover border-2 border-gray-200 dark:border-gray-700"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                        {organizationName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Sua Organização
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Menu de Navegação */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {filteredMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || (item.path === '/reports' && location.pathname.startsWith('/reports/'));

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </div>
                    {item.badge && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.badgeColor || 'bg-blue-600 text-white'}`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Theme Toggle e Logout */}
            <div className="p-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <button
                onClick={toggleTheme}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
              >
                {isDark ? (
                  <>
                    <Sun className="w-5 h-5 mr-3" />
                    Modo Claro
                  </>
                ) : (
                  <>
                    <Moon className="w-5 h-5 mr-3" />
                    Modo Escuro
                  </>
                )}
              </button>
              <Link
                to="/help"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition ${
                  location.pathname === '/help'
                    ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <HelpCircle className="w-5 h-5 mr-3" />
                Ajuda
              </Link>
              <Link
                to="/change-password"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
              >
                <Key className="w-5 h-5 mr-3" />
                Alterar Senha
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sair
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Conteúdo Principal */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top Bar Mobile */}
        <div className="sticky top-0 z-10 md:hidden flex flex-col bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4">
            <button onClick={() => setSidebarOpen(true)} className="flex-shrink-0">
              <Menu className="w-6 h-6 text-gray-700 dark:text-gray-300" />
            </button>
            <div className="flex items-center">
              <img src="/logo192.png" alt="AWFlux" className="w-6 h-6 mr-2 flex-shrink-0 rounded" />
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">AWFlux</h1>
            </div>
            <button onClick={toggleTheme} className="flex-shrink-0">
              {isDark ? (
                <Sun className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              ) : (
                <Moon className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              )}
            </button>
          </div>
          
          {/* Badge de Status de Operação - Mobile */}
          {activeOrganizationId && !user?.is_superuser && (
            <div className="px-4 pb-3">
              <OperatingStatusBadge 
                organizationId={activeOrganizationId} 
                showDetails={false}
                autoRefresh={true}
              />
            </div>
          )}
        </div>

        {/* Badge de Status de Operação - Desktop */}
        {activeOrganizationId && !user?.is_superuser && (
          <div className="hidden md:flex items-center sticky top-0 z-10 h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6">
            <OperatingStatusBadge 
              organizationId={activeOrganizationId} 
              showDetails={true}
              autoRefresh={true}
            />
          </div>
        )}

        {/* 🔴 Alerta de câmeras offline (visível para todos os usuários) */}
        {user?.role !== 'partner' && <OfflineCamerasBanner />}

        {/* 🧪 Banner de período de teste */}
        {trialInfo?.is_trial && (trialInfo.trial_status === 'active' || trialInfo.trial_status === 'expiring_soon') && (
          <div className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${
            trialInfo.trial_status === 'expiring_soon'
              ? 'bg-red-100 text-red-800 dark:bg-red-900/60 dark:text-red-200 border-b border-red-200 dark:border-red-800'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200 border-b border-amber-200 dark:border-amber-800'
          }`}>
            <span>🧪</span>
            <span>
              {trialInfo.trial_status === 'expiring_soon'
                ? `⚠️ Período de Teste expira em ${trialInfo.days_remaining_trial} dia(s)! Fale com seu consultor para contratar o plano.`
                : `Período de Teste ativo — ${trialInfo.days_remaining_trial !== null ? `${trialInfo.days_remaining_trial} dia(s) restante(s)` : 'em andamento'}`}
            </span>
          </div>
        )}

        {/* Conteúdo */}
        <main className="flex-1 bg-gray-50 dark:bg-gray-900 transition-colors">
          {children}
        </main>
      </div>

      {/* Modal de confirmação */}
      <ModalComponent />
    </div>
  );
}
